import { Download, Share, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface InstallAppButtonProps {
  variant?: "default" | "hero";
  className?: string;
}

export const InstallAppButton = ({ variant = "default", className = "" }: InstallAppButtonProps) => {
  const { isInstallable, isInstalled, isIOS, promptInstall } = usePWAInstall();
  const [showIOSDialog, setShowIOSDialog] = useState(false);
  const [showInstructionsDialog, setShowInstructionsDialog] = useState(false);

  const handleInstallClick = async () => {
    if (isIOS) {
      setShowIOSDialog(true);
    } else if (isInstallable) {
      await promptInstall();
    } else {
      // Show generic instructions for desktop users
      setShowInstructionsDialog(true);
    }
  };

  // Don't show if already installed
  if (isInstalled) return null;

  if (variant === "hero") {
    return (
      <>
        <Button
          onClick={handleInstallClick}
          size="lg"
          className={`gap-2 bg-gradient-to-r from-accent to-primary hover:opacity-90 text-white font-semibold px-8 shadow-lg ${className}`}
        >
          <Download className="h-5 w-5" />
          Install Vividad App
        </Button>

        <IOSInstructionsDialog open={showIOSDialog} onOpenChange={setShowIOSDialog} />
        <DesktopInstructionsDialog open={showInstructionsDialog} onOpenChange={setShowInstructionsDialog} />
      </>
    );
  }

  return (
    <>
      <Button
        onClick={handleInstallClick}
        variant="outline"
        size="sm"
        className={`gap-2 border-primary/30 bg-primary/10 hover:bg-primary/20 text-primary ${className}`}
      >
        <Download className="h-4 w-4" />
        <span className="hidden sm:inline">Install App</span>
        <span className="sm:hidden">Install</span>
      </Button>

      <IOSInstructionsDialog open={showIOSDialog} onOpenChange={setShowIOSDialog} />
      <DesktopInstructionsDialog open={showInstructionsDialog} onOpenChange={setShowInstructionsDialog} />
    </>
  );
};

const IOSInstructionsDialog = ({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Smartphone className="h-5 w-5 text-primary" />
          Install Vividad on iPhone
        </DialogTitle>
        <DialogDescription>
          Follow these steps to add Vividad to your home screen:
        </DialogDescription>
      </DialogHeader>
      
      <div className="space-y-4 py-4">
        <div className="flex items-start gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/20 text-primary font-bold text-sm shrink-0">
            1
          </div>
          <div>
            <p className="font-medium">Tap the Share button</p>
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              Look for the <Share className="h-4 w-4 inline" /> icon at the bottom of Safari
            </p>
          </div>
        </div>
        
        <div className="flex items-start gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/20 text-primary font-bold text-sm shrink-0">
            2
          </div>
          <div>
            <p className="font-medium">Scroll and tap "Add to Home Screen"</p>
            <p className="text-sm text-muted-foreground">
              You may need to scroll down in the share menu
            </p>
          </div>
        </div>
        
        <div className="flex items-start gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/20 text-primary font-bold text-sm shrink-0">
            3
          </div>
          <div>
            <p className="font-medium">Tap "Add" to confirm</p>
            <p className="text-sm text-muted-foreground">
              Vividad will appear on your home screen like a real app!
            </p>
          </div>
        </div>
      </div>
      
      <Button onClick={() => onOpenChange(false)} className="w-full">
        Got it!
      </Button>
    </DialogContent>
  </Dialog>
);

const DesktopInstructionsDialog = ({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Download className="h-5 w-5 text-primary" />
          Install Vividad
        </DialogTitle>
        <DialogDescription>
          Add Vividad to your device for quick access:
        </DialogDescription>
      </DialogHeader>
      
      <div className="space-y-4 py-4">
        <div className="p-4 bg-muted/50 rounded-lg">
          <p className="font-medium mb-2">On Chrome / Edge:</p>
          <p className="text-sm text-muted-foreground">
            Look for the install icon (⊕) in the address bar, or click the menu (⋮) → "Install Vividad"
          </p>
        </div>
        
        <div className="p-4 bg-muted/50 rounded-lg">
          <p className="font-medium mb-2">On Android:</p>
          <p className="text-sm text-muted-foreground">
            Tap menu (⋮) → "Add to Home screen" or "Install app"
          </p>
        </div>
        
        <div className="p-4 bg-muted/50 rounded-lg">
          <p className="font-medium mb-2">On iPhone/iPad:</p>
          <p className="text-sm text-muted-foreground">
            In Safari, tap Share → "Add to Home Screen"
          </p>
        </div>
      </div>
      
      <Button onClick={() => onOpenChange(false)} className="w-full">
        Got it!
      </Button>
    </DialogContent>
  </Dialog>
);
